import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { toast } from 'react-toast';
import {
  LuCalendar,
  LuChevronDown,
  LuCircleAlert,
  LuDownload,
  LuFileText,
  LuFolderOpen,
  LuLayoutDashboard,
  LuListTree,
  LuRefreshCw,
  LuShare2,
  LuX,
} from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { Breadcrumbs, Select } from '../ui';
import ShareReportModal from '../reports/ShareReportModal';
import SummaryReport from '../reports/SummaryReport';
import DetailedReport from '../reports/DetailedReport';
import FailureReport from '../reports/FailureReport';
import {
  FILTER_LABELS,
  FILTERS_BY_TYPE,
  REPORTS_QUERY_ROOT,
  useCardData,
  useReportParams,
} from '../reports/reportHooks';
import {
  buildReportPayload,
  DATE_RANGE_OPTIONS,
  downloadReportPdf,
  formatDate,
  formatDateToISO,
  formatPeriodLabel,
  getPresetDateRange,
  isPeriodReady,
  SUITE_EXECUTION_TYPE,
  validateCustomDates,
} from '../reports/reportData';
import { ReportEmptyState } from '../reports/ReportUI';
import '../../styles/reports.css';

const FILTER_VALUE_LABELS = {
  r_type: { plan: 'Test plan', individual: 'Individually' },
};

export default function ReportsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProject: contextProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const { params, update, clearFilters, defaults } = useReportParams();
  const workspaceId = getWorkspaceIdFromURL();

  const projectsQuery = useQuery({
    queryKey: [REPORTS_QUERY_ROOT, 'projects', workspaceId],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `/projects/list_project?pro_status=active&workspace_id=${workspaceId || null}`
      );
      return (response?.projects || []).map((p) => ({ id: p?.project_id, name: p?.project_name }));
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const projects = useMemo(() => projectsQuery.data || [], [projectsQuery.data]);

  const projectId = params.project || contextProject?.id || getProjectIdFromURL() || null;
  const projectName =
    projects.find((p) => String(p.id) === String(projectId))?.name ||
    (String(contextProject?.id) === String(projectId) ? contextProject?.name : '') ||
    'Project';

  const period = useMemo(
    () => ({ range: params.range, from: params.from, to: params.to }),
    [params.range, params.from, params.to]
  );
  const periodReady = isPeriodReady(period);
  const payload = useMemo(
    () => (projectId && periodReady ? buildReportPayload(projectId, period) : null),
    [projectId, period, periodReady]
  );
  const ctx = useMemo(
    () => ({ projectId, payload, enabled: Boolean(projectId && payload) }),
    [projectId, payload]
  );

  const card = useCardData(ctx);
  const refreshing = useIsFetching({ queryKey: [REPORTS_QUERY_ROOT] }) > 0;

  const goTo = useCallback(
    (subPath) => {
      if (workspaceId) navigate(buildWorkspaceDashboardPath(workspaceId, projectId, subPath));
      else navigate(`/dashboard/${subPath}`);
    },
    [navigate, workspaceId, projectId]
  );

  const nav = useMemo(
    () => ({
      openTestCase: (testCaseId) => testCaseId != null && goTo(`test-cases/${testCaseId}`),
      openCaseExecution: (row) => {
        if (row?.execution_id == null) return;
        const qs = new URLSearchParams({
          type: 'case',
          entity_id: String(row.test_case_id ?? ''),
          name: row.test_case_name || 'Execution',
        });
        goTo(`executions/${row.execution_id}?${qs.toString()}`);
      },
      openExecutions: () => goTo('executions'),
      toReport: (type, patch = {}) => update({ type, ...patch }, { push: true }),
    }),
    [goTo, update]
  );

  /* ---------------- Export & share ---------------- */

  const [exporting, setExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const reportIdRef = useRef(`RPT-${Math.floor(1000 + Math.random() * 9000)}`);

  const detailedFilters = useMemo(() => {
    const out = { execution_type: SUITE_EXECUTION_TYPE[params.r_type] ?? SUITE_EXECUTION_TYPE.all };
    if (params.c_status === 'Passed' || params.c_status === 'Failed') {
      out.case_status_filter = params.c_status;
    }
    return out;
  }, [params.r_type, params.c_status]);

  const payloadFor = useCallback(
    (type) => {
      if (!payload) return null;
      if (type === 'summary') return { ...payload };
      if (type === 'failures') {
        return { ...payload, execution_type: SUITE_EXECUTION_TYPE.all, case_status_filter: 'Failed' };
      }
      return { ...payload, ...detailedFilters };
    },
    [payload, detailedFilters]
  );

  const runExport = useCallback(
    async (kind) => {
      const body = payloadFor(kind);
      if (!body) {
        toast.error('Select a project and a valid period before exporting.');
        return;
      }
      setExporting(true);
      try {
        await downloadReportPdf({ kind, payload: body, projectName });
        toast.success('Report exported as PDF');
      } catch (err) {
        console.error('Export failed:', err);
        toast.error(err?.message || 'Failed to export report. Please try again.');
      } finally {
        setExporting(false);
      }
    },
    [payloadFor, projectName]
  );

  const exportOptions = useMemo(() => {
    if (!payload) return [];
    if (params.type === 'summary') {
      return [{ id: 'pdf', label: 'PDF', description: 'Summary report', onSelect: () => runExport('summary') }];
    }
    if (params.type === 'failures') {
      return [{ id: 'pdf', label: 'PDF', description: 'Failed test executions', onSelect: () => runExport('failures') }];
    }
    return [
      {
        id: 'pdf',
        label: 'PDF',
        description: 'Detailed report with current status & execution filters',
        onSelect: () => runExport('detailed'),
      },
    ];
  }, [payload, params.type, runExport]);

  const shareContext = useMemo(() => {
    const body = payloadFor(params.type);
    if (!body) return null;
    return {
      projectId,
      projectName,
      reportId: reportIdRef.current,
      payload: {
        ...body,
        report_type: params.type === 'summary' ? 'summary' : 'detailed',
        execution_type: body.execution_type || SUITE_EXECUTION_TYPE.all,
      },
    };
  }, [payloadFor, params.type, projectId, projectName]);

  /* ---------------- Filters ---------------- */

  const chips = useMemo(() => {
    const out = [];
    if (params.range !== defaults.range) {
      const label =
        params.range === 'custom'
          ? `${formatDate(params.from)} – ${formatDate(params.to)}`
          : DATE_RANGE_OPTIONS.find((o) => o.value === params.range)?.label;
      out.push({
        key: 'range',
        label: 'Period',
        value: label,
        onRemove: () => update({ range: defaults.range, from: '', to: '' }),
      });
    }
    (FILTERS_BY_TYPE[params.type] || []).forEach((key) => {
      if (String(params[key]) === String(defaults[key])) return;
      out.push({
        key,
        label: FILTER_LABELS[key],
        value: FILTER_VALUE_LABELS[key]?.[params[key]] || params[key],
        onRemove: () => update({ [key]: defaults[key] }),
      });
    });
    return out;
  }, [params, defaults, update]);

  const onRefresh = () => queryClient.invalidateQueries({ queryKey: [REPORTS_QUERY_ROOT] });

  const activeTab = REPORT_TABS.find((t) => t.id === params.type);
  const periodLabel = payload ? formatPeriodLabel(payload) : '';

  const panelProps = { ctx, params, update, nav, projectName, periodLabel };

  return (
    <div className="rt-page rpt-page">
      <Breadcrumbs items={[{ label: 'Insights' }, { label: 'Reports' }]} />
      <ReportsHeader
        onRefresh={onRefresh}
        refreshing={refreshing}
        onShare={() => setShareOpen(true)}
        canShare={Boolean(shareContext)}
        exportMenu={<ReportExportMenu options={exportOptions} busy={exporting} />}
      />

      <div className="rpt-controls">
        <ReportTypeSwitcher
          value={params.type}
          onChange={(type) => update({ type }, { push: true })}
          failureCount={card.data?.failed}
        />
        <ReportFilterBar
          projects={projects}
          projectId={projectId}
          onProjectChange={(id) => update({ project: id })}
          period={period}
          onPeriodChange={(next) => update(next)}
          periodLabel={periodLabel}
        />
        <ActiveFilters chips={chips} onClearAll={() => clearFilters(params.type)} />
      </div>

      <div
        id="rpt-panel"
        role="tabpanel"
        aria-labelledby={`rpt-tab-${params.type}`}
        className="rpt-panel"
        key={`${params.type}-${projectId}`}
      >
        {activeTab && <p className="rpt-panel__question">{activeTab.question}</p>}

        {!projectId && (
          <ReportEmptyState
            icon={<LuFolderOpen size={20} />}
            title="Select a project"
            description="Choose a project above to generate its execution reports."
          />
        )}

        {projectId && !periodReady && (
          <ReportEmptyState
            title="Choose a date range"
            description="Pick a start and end date, then select Apply to load the report."
          />
        )}

        {ctx.enabled && params.type === 'summary' && <SummaryReport {...panelProps} />}
        {ctx.enabled && params.type === 'detailed' && <DetailedReport {...panelProps} />}
        {ctx.enabled && params.type === 'failures' && <FailureReport {...panelProps} />}
      </div>

      <footer className="rpt-footer">
        <span>Report {reportIdRef.current}</span>
        <span>Generated {formatDate(new Date().toISOString())} by RobiTest</span>
      </footer>

      <ShareReportModal isOpen={shareOpen} onClose={() => setShareOpen(false)} reportContext={shareContext} />
    </div>
  );
}

export const REPORT_TABS = [
  {
    id: 'summary',
    label: 'Summary',
    icon: LuLayoutDashboard,
    question: 'How did testing perform in this period?',
  },
  {
    id: 'detailed',
    label: 'Detailed',
    icon: LuListTree,
    question: 'What exactly ran, and what happened?',
  },
  {
    id: 'failures',
    label: 'Failures',
    icon: LuCircleAlert,
    question: 'What failed, how often, and where to look first?',
  },
];

export function ReportTypeSwitcher({ value, onChange, failureCount }) {
  const refs = useRef([]);
  const onKeyDown = (e, idx) => {
    let next = null;
    if (e.key === 'ArrowRight') next = (idx + 1) % REPORT_TABS.length;
    if (e.key === 'ArrowLeft') next = (idx - 1 + REPORT_TABS.length) % REPORT_TABS.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = REPORT_TABS.length - 1;
    if (next == null) return;
    e.preventDefault();
    refs.current[next]?.focus();
    onChange(REPORT_TABS[next].id);
  };

  return (
    <div className="rpt-switcher" role="tablist" aria-label="Report type">
      {REPORT_TABS.map((tab, idx) => {
        const active = tab.id === value;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            ref={(n) => {
              refs.current[idx] = n;
            }}
            type="button"
            role="tab"
            id={`rpt-tab-${tab.id}`}
            aria-selected={active}
            aria-controls="rpt-panel"
            tabIndex={active ? 0 : -1}
            className={`rpt-switcher__tab ${active ? 'is-active' : ''} ${tab.id === 'failures' ? 'rpt-switcher__tab--failures' : ''}`}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => onKeyDown(e, idx)}
          >
            <Icon size={15} aria-hidden />
            <span>{tab.label}</span>
            {tab.id === 'failures' && failureCount > 0 && (
              <span className="rpt-switcher__count" aria-label={`${failureCount} failed`}>
                {failureCount > 999 ? '999+' : failureCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function ReportExportMenu({ options, busy }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;
    requestAnimationFrame(() => menuRef.current?.querySelector('button:not([disabled])')?.focus());
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = Array.from(menuRef.current?.querySelectorAll('button:not([disabled])') || []);
        if (!items.length) return;
        e.preventDefault();
        const idx = items.indexOf(document.activeElement);
        const step = e.key === 'ArrowDown' ? 1 : -1;
        items[(idx + step + items.length) % items.length].focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="rpt-export">
      <button
        ref={btnRef}
        type="button"
        className="rt-btn rt-btn--secondary rt-btn--sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        disabled={busy || !options.length}
      >
        {busy ? <span className="rt-btn__spinner" aria-hidden /> : <LuDownload size={14} aria-hidden />}
        <span>{busy ? 'Exporting…' : 'Export'}</span>
        <LuChevronDown size={14} aria-hidden />
      </button>
      {open && (
        <div className="rpt-menu" role="menu" id={menuId} ref={menuRef}>
          {options.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="menuitem"
              className="rpt-menu__item"
              onClick={() => {
                setOpen(false);
                opt.onSelect();
              }}
            >
              <LuFileText size={15} aria-hidden />
              <span className="rpt-menu__text">
                <span className="rpt-menu__label">{opt.label}</span>
                {opt.description && <span className="rpt-menu__desc">{opt.description}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReportFilterBar({ projects, projectId, onProjectChange, period, onPeriodChange, periodLabel }) {
  const [customOpen, setCustomOpen] = useState(period.range === 'custom');
  const [draft, setDraft] = useState({ from: period.from, to: period.to });
  const [errors, setErrors] = useState({ startDate: '', endDate: '' });
  const todayIso = formatDateToISO(new Date());

  useEffect(() => {
    setCustomOpen(period.range === 'custom');
    setDraft({ from: period.from, to: period.to });
    setErrors({ startDate: '', endDate: '' });
  }, [period.range, period.from, period.to]);

  const onRangeChange = (value) => {
    if (value === 'custom') {
      const preset = getPresetDateRange('last30');
      setDraft((d) => ({ from: d.from || preset.start_date, to: d.to || preset.end_date }));
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    onPeriodChange({ range: value, from: '', to: '' });
  };

  const applyCustom = (e) => {
    e?.preventDefault();
    const next = validateCustomDates(draft.from, draft.to);
    setErrors(next);
    if (next.startDate || next.endDate) return;
    onPeriodChange({ range: 'custom', from: draft.from, to: draft.to });
  };

  const cancelCustom = () => {
    setCustomOpen(period.range === 'custom');
    setDraft({ from: period.from, to: period.to });
    setErrors({ startDate: '', endDate: '' });
  };

  const selectValue = customOpen ? 'custom' : period.range;

  return (
    <div className="rpt-filterbar">
      <div className="rpt-filterbar__controls">
        <label className="rpt-field">
          <span className="rpt-field__label">Project</span>
          <Select
            size="sm"
            value={projectId ? String(projectId) : ''}
            onChange={(e) => onProjectChange(e.target.value)}
            aria-label="Project"
            placeholder="Select project"
            className="rpt-field__select rpt-field__select--project"
          >
            {!projectId && <option value="">Select project</option>}
            {projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        </label>

        <label className="rpt-field">
          <span className="rpt-field__label">Period</span>
          <Select
            size="sm"
            value={selectValue}
            onChange={(e) => onRangeChange(e.target.value)}
            aria-label="Report period"
            className="rpt-field__select"
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </label>

        {customOpen && (
          <form className="rpt-custom-range" onSubmit={applyCustom} noValidate>
            <label className="rpt-field">
              <span className="rpt-field__label">From</span>
              <input
                type="date"
                className={`rpt-input ${errors.startDate ? 'is-invalid' : ''}`}
                value={draft.from}
                max={draft.to || todayIso}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                aria-invalid={Boolean(errors.startDate) || undefined}
                aria-describedby={errors.startDate ? 'rpt-from-err' : undefined}
              />
            </label>
            <label className="rpt-field">
              <span className="rpt-field__label">To</span>
              <input
                type="date"
                className={`rpt-input ${errors.endDate ? 'is-invalid' : ''}`}
                value={draft.to}
                min={draft.from || undefined}
                max={todayIso}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                aria-invalid={Boolean(errors.endDate) || undefined}
                aria-describedby={errors.endDate ? 'rpt-to-err' : undefined}
              />
            </label>
            <div className="rpt-custom-range__actions">
              <button type="submit" className="rt-btn rt-btn--primary rt-btn--sm">Apply</button>
              {period.range !== 'custom' && (
                <button type="button" className="rt-btn rt-btn--ghost rt-btn--sm" onClick={cancelCustom}>
                  Cancel
                </button>
              )}
            </div>
            {(errors.startDate || errors.endDate) && (
              <p className="rpt-field__error" role="alert">
                <span id="rpt-from-err">{errors.startDate}</span>
                {errors.startDate && errors.endDate ? ' · ' : ''}
                <span id="rpt-to-err">{errors.endDate}</span>
              </p>
            )}
          </form>
        )}
      </div>
      {periodLabel && (
        <span className="rpt-filterbar__period">
          <LuCalendar size={13} aria-hidden />
          {periodLabel}
        </span>
      )}
    </div>
  );
}

export function ActiveFilters({ chips, onClearAll }) {
  if (!chips.length) return null;
  return (
    <div className="rpt-active-filters" aria-label="Active filters">
      <span className="rpt-active-filters__label">Filters</span>
      {chips.map((chip) => (
        <span className="rpt-chip" key={chip.key}>
          <span className="rpt-chip__key">{chip.label}:</span>
          <span className="rpt-chip__value">{chip.value}</span>
          <button type="button" className="rpt-chip__remove" onClick={chip.onRemove} aria-label={`Remove ${chip.label} filter`}>
            <LuX size={12} aria-hidden />
          </button>
        </span>
      ))}
      <button type="button" className="rpt-link" onClick={onClearAll}>
        Clear all
      </button>
    </div>
  );
}

export function ReportsHeader({ onRefresh, refreshing, onShare, canShare, exportMenu }) {
  return (
    <div className="rpt-header">
      <div className="rpt-header__titles">
        <h1 className="rpt-header__title">Test Execution Reports</h1>
        <p className="rpt-header__desc">
          Understand why results look the way they do — failures, weak suites, slow tests and what changed.
        </p>
      </div>
      <div className="rpt-header__actions">
        <button
          type="button"
          className="rt-btn rt-btn--ghost rt-btn--sm"
          onClick={onRefresh}
          disabled={refreshing}
          aria-label="Refresh report data"
        >
          <LuRefreshCw size={14} aria-hidden className={refreshing ? 'rpt-spin' : undefined} />
          <span>Refresh</span>
        </button>
        <button type="button" className="rt-btn rt-btn--ghost rt-btn--sm" onClick={onShare} disabled={!canShare}>
          <LuShare2 size={14} aria-hidden />
          <span>Share</span>
        </button>
        {exportMenu}
      </div>
    </div>
  );
}
